/**
 * MissingVsLastMonthButton — Dialog "détection exhaustive des oublis".
 *
 * Port BE-TAYE : compare le mois cible au mois précédent, liste les clients
 * facturés le mois précédent et pas encore facturés ce mois-ci.
 */
import { useState } from "react";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  AlertCircle, Loader2, FileSearch, Download, TrendingDown, Calendar,
} from "lucide-react";
import { toast } from "sonner";

const fmtEUR = (v) => new Intl.NumberFormat("fr-FR",
  { style: "currency", currency: "EUR", minimumFractionDigits: 2 }).format(v || 0);
const fmtDate = (d) => d ? new Date(d).toLocaleDateString("fr-FR") : "—";
const fmtMonth = (yyyymm) => {
  if (!yyyymm) return "";
  const [y, m] = yyyymm.split("-").map(Number);
  return new Date(y, m - 1, 1).toLocaleDateString("fr-FR", { month: "long", year: "numeric" });
};

export default function MissingVsLastMonthButton({ month }) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);

  const fetchData = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/vigilance/missing-vs-last-month",
                                       { params: month ? { month } : {} });
      setData(data);
    } catch (e) { toast.error(apiError(e)); }
    finally { setLoading(false); }
  };

  const onOpen = async (v) => {
    setOpen(v);
    if (v && !data) await fetchData();
  };

  const exportCSV = () => {
    if (!data?.clients_missing?.length) return;
    const rows = [["Client", "Nb factures", "Dernière facture", "Total HT", "Total TTC"]];
    data.clients_missing.forEach((c) => {
      const last = c.invoices[0] || {};
      rows.push([c.client, c.invoice_count, last.date || "",
                 c.total_ht.toFixed(2), c.total_ttc.toFixed(2)]);
    });
    const csv = rows.map((r) => r.map((v) => `"${String(v).replace(/"/g, '""')}"`).join(";")).join("\n");
    const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `oublis-facturation-${data.target_month}.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <>
      <Button variant="outline" size="sm" onClick={() => onOpen(true)}
              className="border-orange-300 text-orange-700 hover:bg-orange-50"
              data-testid="missing-vs-last-month-btn">
        <FileSearch className="w-4 h-4 mr-2" /> Détecter les oublis
      </Button>
      <Dialog open={open} onOpenChange={onOpen}>
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-hidden flex flex-col"
                       data-testid="missing-vs-last-month-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertCircle className="w-5 h-5 text-orange-600" /> Oublis de facturation détectés
            </DialogTitle>
            <DialogDescription>
              Clients facturés le mois précédent qui n'ont pas encore été facturés ce mois-ci.
            </DialogDescription>
          </DialogHeader>
          {loading && (
            <div className="flex items-center justify-center py-16">
              <Loader2 className="w-8 h-8 animate-spin text-orange-500" />
            </div>
          )}
          {!loading && data && (
            <>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                <Card className="border-blue-200 bg-blue-50"><CardContent className="p-3">
                  <div className="flex items-center gap-2 text-xs text-blue-700">
                    <Calendar className="w-3.5 h-3.5" /> Mois analysé
                  </div>
                  <p className="text-sm font-semibold text-blue-900 mt-1 capitalize">{fmtMonth(data.target_month)}</p>
                  <p className="text-[10px] text-blue-700 mt-0.5">vs {fmtMonth(data.previous_month)}</p>
                </CardContent></Card>
                <Card className="border-orange-200 bg-orange-50"><CardContent className="p-3">
                  <p className="text-xs text-orange-700">Clients à relancer</p>
                  <p className="text-2xl font-bold text-orange-900">{data.clients_missing_count}</p>
                </CardContent></Card>
                <Card className="border-red-200 bg-red-50"><CardContent className="p-3">
                  <p className="text-xs text-red-700">CA à risque (TTC)</p>
                  <p className="text-2xl font-bold text-red-900">{fmtEUR(data.total_at_risk_ttc)}</p>
                </CardContent></Card>
                <Card><CardContent className="p-3">
                  <p className="text-xs text-slate-600">Volume comparé</p>
                  <p className="text-sm font-semibold text-slate-900 mt-1">
                    <TrendingDown className="w-3.5 h-3.5 inline mr-1 text-slate-500" />
                    {data.invoices_current_month_count} / {data.invoices_previous_month_count}
                  </p>
                  <p className="text-[10px] text-slate-500">factures ce mois / mois précédent</p>
                </CardContent></Card>
              </div>
              <div className="flex-1 overflow-auto border rounded-lg mt-3">
                {data.clients_missing_count === 0 ? (
                  <div className="flex flex-col items-center justify-center py-16 text-center">
                    <div className="p-3 bg-green-100 rounded-full mb-3">
                      <FileSearch className="w-8 h-8 text-green-600" />
                    </div>
                    <p className="text-lg font-semibold text-green-800">Aucun oubli détecté</p>
                  </div>
                ) : (
                  <Table data-testid="missing-clients-table">
                    <TableHeader className="sticky top-0 bg-white z-10">
                      <TableRow>
                        <TableHead className="w-[35%]">Client</TableHead>
                        <TableHead className="text-center">Factures ({fmtMonth(data.previous_month)})</TableHead>
                        <TableHead>Dernière facture</TableHead>
                        <TableHead className="text-right">Montant HT</TableHead>
                        <TableHead className="text-right">Montant TTC</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {data.clients_missing.map((c) => {
                        const last = c.invoices[0] || {};
                        return (
                          <TableRow key={c.client_key} data-testid={`missing-client-row-${c.client}`}
                                     className="hover:bg-orange-50/40">
                            <TableCell className="font-medium">
                              <div className="flex items-center gap-2">
                                <div className="w-2 h-2 rounded-full bg-orange-400 flex-shrink-0" />
                                <span>{c.client}</span>
                              </div>
                            </TableCell>
                            <TableCell className="text-center">
                              <Badge variant="outline" className="bg-orange-50 border-orange-200">{c.invoice_count}</Badge>
                            </TableCell>
                            <TableCell className="text-sm">
                              <div className="font-mono text-xs text-slate-500">{last.numero || "—"}</div>
                              <div>{fmtDate(last.date)}</div>
                            </TableCell>
                            <TableCell className="text-right font-medium">{fmtEUR(c.total_ht)}</TableCell>
                            <TableCell className="text-right font-bold text-red-700">{fmtEUR(c.total_ttc)}</TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                )}
              </div>
              <div className="flex items-center justify-between gap-2 pt-2 border-t">
                <p className="text-xs text-slate-500">
                  Comparaison : présent le mois précédent, absent ce mois-ci.
                </p>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={fetchData} disabled={loading}>
                    Actualiser
                  </Button>
                  <Button variant="outline" size="sm" onClick={exportCSV}
                           disabled={!data.clients_missing_count}
                           data-testid="missing-export-btn">
                    <Download className="w-4 h-4 mr-2" /> Exporter CSV
                  </Button>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
